import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, FolderOpen } from 'lucide-react';
import '@/styles/boost.css';
import Starfield from '@/components/Starfield';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useReveal from '@/hooks/useReveal';
import { useSeo } from '@/lib/seo';
import { CS_IMAGES } from '@/data/imprintCaseStudy';
import { TS_IMAGES } from '@/data/templeCaseStudy';
import { DB_IMAGES } from '@/data/diamondbriteCaseStudy';
import { LDS_HERO } from '@/data/londonDrumsticksCaseStudy';

const CANONICAL = 'https://boostmarketing.agency/work';
const SEO_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Selected Work — BOOST! Design & Marketing',
    description:
      'A portfolio of Boost! Design & Marketing case studies — websites, custom full-stack platforms and full-funnel marketing engineered to perform.',
    url: CANONICAL,
    isPartOf: { '@id': 'https://boostmarketing.agency/#website' },
    hasPart: [
      { '@type': 'CreativeWork', name: 'London Drumstick Co. — bespoke e-commerce & operations platform', url: 'https://boostmarketing.agency/case-studies/london-drumstick-co' },
      { '@type': 'CreativeWork', name: 'Imprint MIS — website & custom platform build', url: 'https://boostmarketing.agency/case-studies/imprint-mis' },
      { '@type': 'CreativeWork', name: 'Temple of Sound — bespoke full-stack retail platform', url: 'https://boostmarketing.agency/case-studies/temple-of-sound' },
      { '@type': 'CreativeWork', name: 'Diamondbrite — reputation & ecommerce growth', url: 'https://boostmarketing.agency/case-studies/diamondbrite' },
    ],
    inLanguage: 'en-GB',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://boostmarketing.agency/' },
      { '@type': 'ListItem', position: 2, name: 'Work', item: CANONICAL },
    ],
  },
];

const CASE_STUDIES = [
  {
    id: 'londondrumsticks',
    badge: 'Full-Stack Case Study',
    title: 'A direct-to-drummer store, built as one system',
    client: 'London Drumstick Co. · Drumsticks & Merch · UK',
    desc: 'A bespoke e-commerce and business-operations platform — mobile-first storefront, a made-to-order custom stick builder, loyalty, an endorsed-artist programme and a full back-office that replaced the spreadsheets, inboxes and manual courier bookings.',
    tags: ['Ecommerce', 'Product Configurator', 'CRM & Support', 'Fulfilment Automation'],
    to: '/case-studies/london-drumstick-co',
    image: LDS_HERO.image,
    url: 'londondrumsticks.co.uk',
    reverse: false,
    temple: false,
  },
  {
    id: 'imprint',
    badge: 'Flagship Case Study',
    title: 'Reimagining the future of intelligent printing',
    client: 'Imprint MIS · Print & Packaging Software · UK',
    desc: 'A ground-up website and custom full-stack platform rebuild — faster, smarter, and engineered to be found by both people and AI. Headless CMS, CRM, analytics and a GEO-first build measured live.',
    tags: ['Web Design', 'Full-Stack Build', 'SEO / GEO', 'Branding'],
    to: '/case-studies/imprint-mis',
    image: CS_IMAGES.hero,
    url: 'imprint-mis.co.uk',
    reverse: false,
    temple: false,
  },
  {
    id: 'temple',
    badge: 'Full-Stack Case Study',
    title: 'Two legends, one house',
    client: 'Temple of Sound · Cymbals & Drumsticks · UK',
    desc: 'A 100% bespoke retail platform for the UK’s premier drum-product distributor — B2C shop, B2B trade portal, three-tier membership, two custom loyalty currencies and an interactive drumstick builder.',
    tags: ['Web Design', 'Ecommerce', 'B2B Portal', 'Loyalty Engine'],
    to: '/case-studies/temple-of-sound',
    image: TS_IMAGES.hero,
    url: 'templeofsound.co.uk',
    reverse: true,
    temple: true,
  },
  {
    id: 'diamondbrite',
    badge: 'Growth Case Study',
    title: 'From 2.8★ to 4.9★ — a reputation transformed',
    client: 'Diamondbrite · Vehicle Care & Paint Protection · UK',
    desc: 'A multi-year strategic alliance: automated review generation, ecommerce, loyalty, photo-realistic 3D product renders and full-funnel marketing that took Diamondbrite from 4 reviews to 422+ at 4.9★.',
    tags: ['Reputation', 'Ecommerce', 'Brand', 'Full-Funnel Marketing'],
    to: '/case-studies/diamondbrite',
    image: DB_IMAGES.hero,
    url: 'diamondbrite.co.uk',
    reverse: false,
    temple: false,
  },
];

function Reveal({ children, className = '', delay = 0, ...rest }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {children}
    </div>
  );
}

function FeaturedCard({ cs }) {
  return (
    <Reveal>
      <Link
        to={cs.to}
        className={`work-featured ${cs.reverse ? 'work-featured-reverse' : ''} ${cs.temple ? 'work-featured-temple' : ''}`}
        data-testid={`work-featured-${cs.id}`}
        style={{ textDecoration: 'none' }}
      >
        <div className="work-featured-body">
          <span className="work-featured-badge"><Sparkles size={14} /> {cs.badge}</span>
          <h2 className="work-featured-title">{cs.title}</h2>
          <p className="work-featured-client">{cs.client}</p>
          <p className="work-featured-desc">{cs.desc}</p>
          <div className="work-tags">
            {cs.tags.map((t) => <span className="work-tag" key={t}>{t}</span>)}
          </div>
          <span className="boost-btn boost-btn-primary">
            Read the case study <ArrowRight size={16} />
          </span>
        </div>
        <div className="work-featured-media">
          <figure className="browser-shot" style={{ margin: 0 }}>
            <div className="browser-chrome">
              <span className="browser-dot" /><span className="browser-dot" /><span className="browser-dot" />
              <span className="browser-url">{cs.url}</span>
            </div>
            <img src={cs.image} alt={cs.title} loading="lazy" />
          </figure>
        </div>
      </Link>
    </Reveal>
  );
}

export default function WorkIndex() {
  useSeo({
    title: 'Our Work — Case Studies & Portfolio | Boost! Design & Marketing',
    description:
      'Selected work from Boost! Design & Marketing — websites, custom full-stack platforms and full-funnel marketing. Explore our Imprint MIS and Temple of Sound case studies.',
    canonical: CANONICAL,
    image: 'https://boostmarketing.agency/og-work.png',
    jsonLd: SEO_JSONLD,
  });
  const total = CASE_STUDIES.length;
  return (
    <div className="boost-app work-page" data-testid="work-index">
      <Starfield />
      <Navbar />
      <main>
        <section className="work-hero">
          <div className="work-hero-inner">
            <Reveal>
              <span className="section-eyebrow">// MISSION LOG</span>
              <h1 className="hero-title" style={{ fontSize: 'clamp(44px, 6vw, 92px)', marginBottom: 18 }}>
                Selected <span className="hero-line-pink">Work</span>
              </h1>
              <p className="section-sub" style={{ maxWidth: 640 }}>
                A look at the brands we&rsquo;ve helped reach orbit &mdash; websites, custom platforms and
                full-funnel marketing engineered to perform.
              </p>
              <span className="work-count"><FolderOpen size={14} /> {total} featured missions</span>
            </Reveal>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 44 }}>
          <div className="section-inner">
            {CASE_STUDIES.map((cs) => <FeaturedCard key={cs.id} cs={cs} />)}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
